import React from "react";

function PrivacyPolicy() {
  return (
    <div className="page">
      <style>{`
        .page {
          --bg: #0b0f14;
          --muted: #93a4b3;
          --text: #e5e7eb;
          --card: rgba(255, 255, 255, 0.06);
          --border: rgba(255, 255, 255, 0.09);
          --accent: #22c55e;
          min-height: 100vh;
          background:
            radial-gradient(40% 50% at 85% 10%, rgba(34, 197, 94, 0.15) 0%, rgba(0,0,0,0) 60%),
            radial-gradient(30% 35% at 10% 20%, rgba(34, 197, 94, 0.1) 0%, rgba(0,0,0,0) 60%),
            linear-gradient(180deg, #0b0f14 0%, #070b10 60%, #05070a 100%);
          color: var(--text);
        }

        .container { max-width: 1100px; margin: 0 auto; padding: 24px; }

        .card {
          max-width: 860px;
          margin: 48px auto;
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
          line-height: 1.7;
        }

        .title {
          font-size: clamp(28px, 5vw, 44px);
          line-height: 1.1;
          letter-spacing: -0.02em;
          margin: 8px 0 8px;
          background: linear-gradient(180deg, #ffffff, #b9c2ca 60%, #7b8794);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }

        .muted { color: var(--muted); }
        h2 { font-size: 18px; margin: 18px 0 8px; }
        p { margin: 10px 0; }
        ul { margin: 8px 0 16px 18px; }
        li { margin: 6px 0; }
        a { color: #86efac; text-decoration: underline; }
      `}</style>

      <div className="container">
        <section className="card">
          <h1 className="title">Privacy Policy</h1>
          <p className="muted">
            <strong>Effective:</strong> 2025-10-07
          </p>

          <p>
            This Privacy Policy explains how <strong>Olin Finance</strong>{" "}
            ("we", "us", or "our") collects, uses, discloses, and safeguards
            your information when you use our expense tracking and analysis
            application (the "Service"). Our Service helps you log expenses and
            uses AI to analyze and provide insights. By using the Service, you
            agree to the collection and use of information in accordance with
            this policy.
          </p>

          <h2>Information We Collect</h2>
          <ul>
            <li>
              <strong>Account Information:</strong> name, email address,
              password (hashed), authentication tokens.
            </li>
            <li>
              <strong>Financial/Expense Data:</strong> expense descriptions,
              amounts, categories, dates, vendors, notes, attachments you choose
              to upload.
            </li>
            <li>
              <strong>Subscription & Billing:</strong> plan details, billing
              status, and limited payment metadata from our payment processor.
              We do not store full card numbers.
            </li>
            <li>
              <strong>Usage Data:</strong> app interactions, feature usage,
              crash reports, and diagnostics.
            </li>
            <li>
              <strong>Device/Technical Data:</strong> IP address, browser type,
              device identifiers, operating system.
            </li>
            <li>
              <strong>Cookies & Similar Technologies:</strong> to keep you
              signed in, remember preferences, and measure performance.
            </li>
          </ul>

          <h2>How We Use Your Information</h2>
          <ul>
            <li>Provide, maintain, and improve the Service.</li>
            <li>
              Analyze expenses using AI models to deliver insights and
              recommendations.
            </li>
            <li>Personalize your experience and surface relevant analytics.</li>
            <li>Manage subscriptions, payments, and account support.</li>
            <li>
              Detect, prevent, and address fraud, abuse, and security incidents.
            </li>
            <li>Comply with legal obligations.</li>
            <li>
              Communicate with you about updates, security alerts, and support.
            </li>
          </ul>

          <h2>AI and Automated Processing</h2>
          <p>
            We use AI models and service providers to analyze expense data and
            generate insights. Where required by law, we rely on your consent or
            our legitimate interests to perform this processing. You can request
            human review of decisions that significantly affect you, object to
            certain processing, or disable specific AI features in your settings
            where available.
          </p>

          <h2>Legal Bases (EEA/UK Users)</h2>
          <ul>
            <li>
              <strong>Contract:</strong> processing necessary to provide the
              Service you requested.
            </li>
            <li>
              <strong>Legitimate Interests:</strong> to secure, improve, and
              personalize the Service.
            </li>
            <li>
              <strong>Consent:</strong> for non-essential cookies, marketing,
              and certain AI features where required.
            </li>
            <li>
              <strong>Legal Obligation:</strong> to comply with applicable laws
              and regulations.
            </li>
          </ul>

          <h2>Sharing of Information</h2>
          <ul>
            <li>
              <strong>Processors:</strong> hosting, analytics, email delivery,
              error monitoring, and AI service providers who act on our
              instructions.
            </li>
            <li>
              <strong>Payments:</strong> our payment processor handles payment
              information for subscriptions; we receive limited billing
              metadata.
            </li>
            <li>
              <strong>Business Transfers:</strong> in connection with a merger,
              acquisition, or asset sale.
            </li>
            <li>
              <strong>Legal:</strong> to comply with law, enforce terms, or
              protect rights, property, and safety.
            </li>
            <li>
              <strong>With Your Direction:</strong> when you connect third-party
              integrations.
            </li>
          </ul>

          <h2>Data Retention</h2>
          <p>
            We retain personal data for as long as needed to provide the
            Service, comply with legal obligations, resolve disputes, and
            enforce agreements. You can request deletion of your account data;
            we will delete or anonymize data unless we must retain it by law or
            for legitimate business purposes.
          </p>

          <h2>Security</h2>
          <p>
            We implement technical and organizational measures designed to
            protect your information. No method of transmission over the
            Internet or electronic storage is 100% secure; we cannot guarantee
            absolute security.
          </p>

          <h2>International Transfers</h2>
          <p>
            Your information may be processed in countries other than your own.
            Where required, we use appropriate safeguards such as Standard
            Contractual Clauses to protect cross-border transfers.
          </p>

          <h2>Your Rights</h2>
          <ul>
            <li>Access, correct, or delete your personal data.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Data portability where applicable.</li>
            <li>
              Withdraw consent at any time where processing is based on consent.
            </li>
            <li>
              Lodge a complaint with your local data protection authority.
            </li>
          </ul>

          <h2>Children’s Privacy</h2>
          <p>
            The Service is not directed to children under 13 (or under 16 in the
            EEA where applicable), and we do not knowingly collect personal
            information from children.
          </p>

          <h2>Cookies and Preferences</h2>
          <p>
            You can manage cookie preferences through your browser settings.
            Where required, we will request your consent for non-essential
            cookies.
          </p>

          <h2>Third-Party Links</h2>
          <p>
            The Service may contain links to third-party sites. We are not
            responsible for their privacy practices. We encourage you to review
            their policies.
          </p>

          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post
            the updated policy and update the "Effective" date above. If changes
            are material, we may provide additional notice as required by law.
          </p>

          <h2>Contact Us</h2>
          <p>
            If you have questions or requests, contact us at{" "}
            <strong>contact@olinfinance.com</strong> or by mail at
            <strong> Olin Finance</strong>. If applicable, you may contact our
            Data Protection Officer at
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
